/* eslint-disable @typescript-eslint/no-explicit-any */
import { configureStore, createSlice, Reducer } from '@reduxjs/toolkit';

import { authReducer, INITIAL_AUTH_STATE } from '@/redux/auth/reducer';
import { configsReducer, setTenant } from '@/redux/configs';
import { fetchTerritories } from '@/redux/countries/actions';
import { territoryDataReducer } from '@/redux/countries/reducer';
import {
  documentSigningReducer,
  fetchSigningDocuments,
  INITIAL_DOCUMENT_SIGNING_STATE,
} from '@/redux/documentSigning';
import {
  INITIAL_OPERATION_DATA_STATE,
  operationDataReducer,
} from '@/redux/operation';
import { INITIAL_UI_STATE, uiReducer } from '@/redux/ui';
import { fetchAccountTypes, fetchPEPQuestions } from '@/redux/user-data';
import {
  INITIAL_USER_DATA_STATE,
  userDataReducer,
} from '@/redux/user-data/reducer';

import { AccountType, AccountTypeStatus } from '@/types/api/account';
import { PEPAnswerStatus, QuestionCode } from '@/types/api/pep';
import { Tenant } from '@/types/common';
import {
  Document,
  DocumentSection,
  DocumentStatus,
  DocumentType,
} from '@/types/document';
import {
  ApplicationType,
  EmploymentStatus,
  HolderType,
  ICBResponseCode,
  User,
  UserStatus,
} from '@/types/user';

import { BASE_ACCOUNT_TYPES } from './fixtures/account-types';
import { DOC_SIGN_FORMS_RESPONSE } from './fixtures/docSignForms';
import { PEP_QUESTIONS_RESPONSE } from './fixtures/pepQuestion';
import { TERRITORIES_RESPONSE } from './fixtures/territories';

export function createMockDocument(data?: Partial<Document>): Document {
  return {
    id: String(new Date().getTime()),
    fileName: `${String(new Date().getTime())}.jpg`,
    type: DocumentSection.ProofOfAddress,
    createdAt: '2024-06-24T12:05:12.632894Z',
    status: DocumentStatus.InReview,
    uri: 'test uri',
    ...data,
  };
}

export function createMockMinor({
  data,
  documents = [],
}: {
  data?: Partial<User>;
  documents?: Document[];
} = {}): User {
  return createMockUser({
    data: {
      applicationType: ApplicationType.JOINT,
      holderType: HolderType.JointMinor,
      birthday: getLessThan18Birthday(),
      ...data,
    },
    documents,
  });
}

export function createMockAdult({
  data,
  documents = [],
}: {
  data?: Partial<User>;
  documents?: Document[];
} = {}): User {
  return createMockUser({
    data: {
      applicationType: ApplicationType.JOINT,
      holderType: HolderType.JointAdult,
      birthday: '1980-01-31',
      ...data,
    },
    documents,
  });
}

export function createMockUser({
  data,
  documents = [],
}: {
  data?: Partial<User>;
  documents?: Document[];
} = {}): User {
  return {
    id: String(new Date().getTime()),
    phoneNumber: '1284123456789',
    email: 'abc@example.com',
    username: 'mockUser',
    firstName: 'John',
    lastName: 'Doe',
    birthday: '1989-11-20',
    citizenship: 'ARG',
    taxResidences: 'USA',
    territoryCode: 'USA',
    branchName: 'USA',
    status: UserStatus.InReview,
    rim: '12345',
    icbResponseCode: 0,
    accountTypes: [
      {
        id: 'd911d321-258d-46c6-8368-37b8446a9530',
        name: 'Personal Chequing Account',
        currency: 'USD',
        primaryAccount: true,
        secondaryAccount: false,
        operationId: '1234567890',
        status: AccountTypeStatus.Active,
        type: AccountType.Checking,
      },
    ],
    pepAnswerStatus: PEPAnswerStatus.NotAnswered,
    termsAndConditions: true,
    addressDetailed: {
      line1: '10600 Highland Springs Avenue',
      city: 'Beaumont',
      state: 'California',
      zip: '92223',
      country: 'USA',
    },
    employmentDetailed: {
      employmentStatus: EmploymentStatus.Employed,
    },
    documents: [
      ...documents,
      createMockDocument({
        vuOperationId: 1234,
        vuOperationGuid: String(new Date().getTime()),
        type: DocumentSection.Selfie,
      }),
      createMockDocument({
        vuOperationId: 1234,
        vuOperationGuid: String(new Date().getTime()),
        type: DocumentSection.Identity,
        docType: DocumentType.Id,
        docNumber: '12345678',
        expiryDate: '2040-10-08',
      }),
    ],
    tenant: Tenant.BVI,
    ...data,
  };
}

export function createMockReducer(
  initialState: object = {},
  name: string = 'mock-reducer',
) {
  const slice = createSlice({
    name,
    initialState,
    reducers: {},
  });

  return slice.reducer;
}

export function createMockStore(reducer: Record<string, Reducer>) {
  const mockStore = configureStore({
    reducer: reducer,
  });

  return mockStore;
}

export function createMockFullStore({
  userData,
  user,
  operationData,
  configs,
  documentSigning,
  tenant: rawTenant,
}: any = {}) {
  const tenant =
    rawTenant ||
    user?.tenant ||
    userData?.user?.tenant ||
    configs?.tenant ||
    Tenant.BVI;

  const mockUser = createMockUser({
    data: {
      tenant: tenant,
      phoneNumber: '+1234567890',
      pepAnswered: [QuestionCode.Personal], // Use Personal question code so third-party compliance is answered
      documents: [
        createMockDocument({
          type: DocumentSection.Identity,
          uri: 'test-id.jpg',
        }),
        createMockDocument({
          type: DocumentSection.Selfie,
          uri: 'test-selfie.jpg',
        }),
        createMockDocument({
          type: DocumentSection.IDVideo,
          uri: 'test-video.mp4',
          idmResultCode: 'PASSED',
        }),
      ],
      username: 'testUser',
      taxResidences: 'US',
      icbResponseCode: ICBResponseCode.Success,
      status: UserStatus.InReview,
      accountTypes: [
        {
          id: '1',
          name: 'Premium',
          currency: 'USD',
          operationId: '123',
          status: 'Active' as any,
          type: 'Checking' as any,
        },
      ],
      operationId: '123',
      ...(userData ?? {}),
      ...(user ?? {}),
    },
  });

  const mockOperationUser = operationData?.user
    ? createMockMinor({
        data: {
          tenant: tenant,
          ...(operationData?.user ?? {}),
        },
      })
    : undefined;

  const store = configureStore({
    reducer: {
      auth: authReducer,
      configs: configsReducer,
      ui: uiReducer,
      userData: userDataReducer,
      operationData: operationDataReducer,
      territoryData: territoryDataReducer,
      documentSigning: documentSigningReducer,
    },
    preloadedState: {
      auth: {
        ...INITIAL_AUTH_STATE,
        user: mockUser,
      },
      configs: {
        features: [],
        occupations: [{ value: 'ENG', text: 'Engineer' }],
        directorOccupations: [],
        businessCategories: [
          {
            value: '101',
            text: 'Technology',
          },
        ],
        businessCategoriesById: {
          '101': {
            value: '101',
            text: 'Technology',
          },
        },
        ...(configs ?? {}),
      },
      ui: {
        ...INITIAL_UI_STATE,
      },
      userData: {
        ...INITIAL_USER_DATA_STATE,
        user: mockUser,
        holders: mockOperationUser
          ? [
              {
                clientId: mockOperationUser?.id,
                firstName: 'DUC',
                middleName: 'ZOE',
                lastName: 'DUC',
                email: 'child_1@yopmail.com',
                holderType: 'JOINT_MINOR',
              },
              {
                clientId: mockUser?.id,
                firstName: 'John',
                middleName: 'ZOE',
                lastName: 'Van Does',
                email: 'parent_1@yopmail.com',
                holderType: 'JOINT_ADULT',
              },
            ]
          : [],
        ...(userData ?? {}),
      },
      operationData: {
        ...INITIAL_OPERATION_DATA_STATE,
        ...(operationData ?? {}),
        user: mockOperationUser,
      },
      documentSigning: {
        ...INITIAL_DOCUMENT_SIGNING_STATE,
        ...(documentSigning ?? {}),
      },
    },
  });

  store.dispatch({
    type: fetchTerritories.fulfilled.type,
    payload: TERRITORIES_RESPONSE,
  });
  store.dispatch({
    type: fetchAccountTypes.fulfilled.type,
    payload: BASE_ACCOUNT_TYPES,
  });

  store.dispatch({
    type: fetchSigningDocuments.fulfilled.type,
    payload: DOC_SIGN_FORMS_RESPONSE,
  });

  store.dispatch({
    type: fetchPEPQuestions.fulfilled.type,
    payload: PEP_QUESTIONS_RESPONSE,
  });
  store.dispatch(setTenant({ tenant: tenant }));

  return store;
}

function getLessThan18Birthday() {
  const lessThan18Birthday = new Date(
    new Date().setFullYear(new Date().getFullYear() - 17),
  );

  return lessThan18Birthday.toISOString().split('T')[0];
}

export const mockCityOptions = [
  { text: 'Port of Spain', value: 'Port of Spain' },
  { text: 'San Fernando', value: 'San Fernando' },
];

export function createStoreWithCityOptions({
  tenant = Tenant.Guyana,
  user,
  addressCityOptions = mockCityOptions,
}: {
  tenant?: Tenant;
  user?: Record<string, unknown>;
  addressCityOptions?: typeof mockCityOptions;
} = {}) {
  return createMockFullStore({
    user: { tenant, ...user },
    configs: { addressCityOptions },
    tenant,
  });
}

export function createStoreWithoutCityOptions(tenant: Tenant = Tenant.BVI) {
  return createMockFullStore({
    user: { tenant },
    tenant,
  });
}
